// One permission matrix + one copy helper for message actions on EVERY
// surface (channels, character DMs, stories, user DMs). The per-surface views
// wire through this module so "can she edit/delete this?" can never drift
// apart between surfaces again (2026-07-27: "she needs to be able to edit and
// delete her messages everywhere from mobile and web").
//
// These predicates mirror the live RLS policies — the DB remains the actual
// enforcer; this just keeps the UI honest about what will succeed:
//   channel_messages:   own OR admin OR editor-in-private-channel (co-edit)
//   dm_messages:        own conversation (owner edits anything in it)
//   story_messages:     own story (owner edits anything in it)
//   user_dm_messages:   sender only
//   group_chat_messages: any member of the group (co-edit, incl. AI rows —
//                        policies gcm_update/gcm_delete, 20260731191000)

export function canEditMessage({ surface, msg, userId, mode = 'private' }) {
  if (!msg || msg._optimistic) return false;
  switch (surface) {
    case 'channel':
      if (msg.user_id && msg.user_id === userId) return true;
      return mode === 'private'; // co-edit: both players own the fiction (incl. AI rows)
    case 'dm':
    case 'story':
      return true; // rendered only inside the owner's own conversation/story
    case 'group':
      // Rendered only inside a group the viewer belongs to, and both players
      // co-own the scene (Nicole, 2026-07-31: no edit/delete/regen existed).
      return true;
    case 'userdm':
      return !!msg.sender_id && msg.sender_id === userId;
    default:
      return false;
  }
}

export function canDeleteMessage({ surface, msg, userId, mode = 'private' }) {
  // Same matrix as editing today; kept as a separate predicate so the rules
  // can diverge later (e.g. delete-window limits) without touching call sites.
  return canEditMessage({ surface, msg, userId, mode });
}

// Copy ONE message's exact text. Async clipboard first; execCommand fallback
// for older iOS webviews; never throws — returns whether the copy happened so
// callers can flash a ✓.
export async function copyMessageText(content) {
  const text = String(content || '');
  if (!text) return false;
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to execCommand */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}

// Double-tap detection for touch surfaces (replaces the ⋯ chips, 2026-07-27).
// Two taps on the SAME message within 320ms and 24px = open the action card.
// Distance guard keeps scroll flicks from triggering; same-id guard keeps
// fast taps on adjacent messages independent.
export function isDoubleTap(prev, cur, { ms = 320, px = 24 } = {}) {
  if (!prev || !cur || prev.id !== cur.id) return false;
  if (cur.t - prev.t > ms || cur.t - prev.t <= 0) return false;
  return Math.abs(cur.x - prev.x) <= px && Math.abs(cur.y - prev.y) <= px;
}
